import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AuthenticationService } from '@security/authentication.service';
import { LoaderService } from './loader/loader.service';
import { GenericErrorModalComponent } from '@container/modal/generic-error-modal/generic-error-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { EnvironmentService } from '../environment.service';

@Injectable()
export class RESTInterceptor implements HttpInterceptor {
  requestNum = 0;

  constructor(
    private dialog: MatDialog,
    private loaderService: LoaderService,
    private environmentService: EnvironmentService,
    private authenticationService: AuthenticationService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authenticationService.getAccessToken();
    let finalReq = req.clone({
      withCredentials: true,
      headers: token ? req.headers.set('Authorization', `Bearer ${token}`) : req.headers,
    });
    this.requestNum++;

    const timer = setTimeout(() => {
      this.showLoader();
    }, 0.1 * 1000);

    if (this.environmentService.dev) {
      finalReq = finalReq.clone({ method: 'GET' });
    }

    return next.handle(finalReq).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403)) {
          return from(this.authenticationService.refreshToken()).pipe(
            switchMap((ok) => {
              if (ok) {
                const newToken = this.authenticationService.getAccessToken();
                let retryReq = req.clone({
                  withCredentials: true,
                  headers: newToken ? req.headers.set('Authorization', `Bearer ${newToken}`) : req.headers,
                });
                if (this.environmentService.dev) {
                  retryReq = retryReq.clone({ method: 'GET' });
                }
                return next.handle(retryReq);
              }
              this.authenticationService.login();
              return throwError(() => err);
            }),
          );
        }

        if (err instanceof HttpErrorResponse) {
          const errDialog = this.dialog.open(GenericErrorModalComponent, {
            width: '350px',
            data: err.error,
          });
          errDialog.afterClosed().subscribe(() => {
            this.hideLoader();
            this.requestNum = 0;
          });
        }
        return throwError(() => err);
      }),
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          this.requestNum--;
          if (this.requestNum <= 0) {
            this.hideLoader();
            this.requestNum = 0;
          }
          clearTimeout(timer);
        }
      }),
    );
  }

  private showLoader(): void {
    this.loaderService.show();
  }

  private hideLoader(): void {
    this.loaderService.hide();
  }
}
